import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer transition-transform transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				// Default button using moss-green
				default:
					"bg-moss-green-80 text-white hover:bg-moss-green dark:bg-moss-green-80 dark:hover:bg-moss-green",
				// Secondary button using pistachio
				secondary:
					"bg-pistachio-50 text-moss-green-80 hover:bg-pistachio dark:bg-pistachio dark:hover:bg-pistachio-85",
				// Tertiary button using gold
				tertiary:
					"bg-mustard-70 text-moss-green-80 hover:bg-mustard dark:bg-mustard dark:hover:bg-mustard-80",
				// Destructive button using poppy
				destructive:
					"bg-poppy-60 text-white hover:bg-poppy dark:bg-poppy dark:hover:bg-poppy-80",

				outline:
					"border border-zinc-200 bg-white hover:bg-zinc-100 hover:text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-50",
				ghost:
					"hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-50",
				link: "text-moss-green underline-offset-4 hover:underline dark:text-gold",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "h-10 w-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	}
);
Button.displayName = "Button";

export { Button, buttonVariants };
